import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import ModalBody from '../../../modals/modalBody';
import { useTranslate } from '@tolgee/react';
import {
  cancelImageThumbnail,
  clearEditedMediafile,
  setModalUiContent,
  setShowEditImageModal,
  updateBrandEditorCloseState,
  updateEditorCloseState,
  updateMultiImageEditorCloseState,
  updateOverlayCreatorCloseState
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import SchedulingModal from '../Scheduling';
import PlanningConfirmation from '../../cards/planningConfirmation';
import ThumbnailPicker from '../thumbnailPicker';
import ImagesEditor from '../imagesEditor';
import EditorConfirmation from '../../cards/editorExitConfirmation';
import { ALL, PLATFORM_LIST } from '../../../../config/constants';
import MultiImageModifier from '../MultiImageAutoModifier';
import ThumbnailEditorConfirmation from '../../cards/thumbnailEditorConfirmation';
import IGTags from './InstagramTags';
import IGProductTags from './InstagramProductsTags';
import InstagramMusic from './InstagramMusic';
import BrandImagesEditor from '../brandImagesEditor';
import OverlayCreator from '../overlayCreator';
import OverlayEditorConfirmation from '../../cards/overlayEditorConfirmation';
import MediaAltTags from '../mediaAltTags';
import ContentTextBlocks from '../contentTextBlocks';
import { setTextBlockModalUi } from '../../../../redux/slices/textBlocks';
import MediaView from '../mediaView';
import BrowserPluginModal from './browserPluginModal';

const CreatorMainModals = ({ selectedEditorPlatform }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const [showSchedule, setShowSchedule] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [showAltTagsConf, setShowAltTagsConf] = useState(false);
  const [showBrandConf, setShowBrandConf] = useState(false);
  const [showEditorConfirmation, setShowEditorConfirmation] = useState(false);
  const [productTagConf, setProductTagConf] = useState(false);
  const [showThumbnailConf, setShowThumbnailConf] = useState(false);
  const [showOverlayConf, setShowOverlayConf] = useState(false);
  const [showBrowserPluginConf, setShowBrowserPluginConf] = useState(false);

  const scheduleBtnRef = useRef(null);
  const scheduleModalToggle = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.schedule.toggle
  );
  const recurrence = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.schedulePlanningData.recurrence_as_str
  );

  const savedRecurrence = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.recurrence_as_str
  );

  const time = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.time
  );

  const thumbnailStates = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.thumbnail
  );
  const overlayStates = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.brandOverlay
  );
  const IGTagModalState = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.modalStates.IGTags
  );

  const IGProductTagModalState = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.IGProductTags
  );

  const IGMusicModalState = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.IGMusic
  );

  const mediaStates = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.mediaEditor
  );
  const mediaAltTagsStates = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.mediaAltTags
  );

  const { mediaViewer } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.modalStates
  );
  const brandMediaModal = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.brandMediaEditor
  );
  const { browserPlugin } = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.modalStates
  );
  const EditorChanged = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.allowEditorClose
  );

  const BrandEditorChanged = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.allowBrandEditorClose
  );
  const OverlayEditorChanged = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.allowOverlayCreatorClose
  );

  const multiAutoMediaModal = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.multiMediaEditor
  );
  const multiEditorChanged = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.allowMultiImageEditor
  );

  const postData = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.postData
  );
  const slice = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator
  );
  const platformPostData = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData?.[multiAutoMediaModal.platform]
  );
  const platformEnabled = useSelector(
    (state: lomavisCreatorRootState) => state.lomavisCreator.platformEnabled
  );

  const thumbnailPlatform = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.modalStates.thumbnail.platform
  );

  const layeredThumbnail = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[thumbnailPlatform]?.layeredImageThumbnail
  );

  const textBlock = useSelector(
    (state: any) => state.textBlocks.textBlockModal
  );

  const checkSchedulingInfo = () => {
    let checkArray = [];

    for (const p of PLATFORM_LIST) {
      if (platformEnabled[p]) {
        if (
          // compare in UTC to avoid timezone issues
          new Date(postData[p].tempPublicationData.datetime).toISOString() ===
            new Date(postData[p].planned_publication_datetime).toISOString() &&
          slice.temporaryPublishingDelayData.totalMinutes ===
            slice.publishingDelayData.totalMinutes &&
          slice.temporaryPublishingDelayData.noPublishDays ===
            slice.publishingDelayData.noPublishDays
        ) {
          checkArray.push(true);
        } else {
          checkArray.push(false);
        }
      }
    }
    console.log('the response', checkArray);
    if (checkArray.includes(false)) {
      return false;
    } else {
      return true;
    }
  };
  const closeSchedulingModal = () => {
    if (checkSchedulingInfo() && savedRecurrence === recurrence) {
      dispatch(
        setModalUiContent({
          modal: 'schedule',
          attribute: 'toggle',
          value: !scheduleModalToggle
        })
      );
    } else {
      scheduleBtnRef.current.click();
    }
  };
  return (
    <div className="w-100">
      {scheduleModalToggle && (
        <ModalBody
          onClose={() => {
            closeSchedulingModal();
          }}
        >
          <SchedulingModal
            onClose={() => {
              closeSchedulingModal();
            }}
          />
          <button
            type="button"
            className="btn btn-primary d-none"
            onClick={() => setShowSchedule(true)}
            ref={scheduleBtnRef}
          >
            Launch demo modal
          </button>
          <PlanningConfirmation
            handleClose={() => setShowSchedule(false)}
            handleShow={() => setShowSchedule(false)}
            show={showSchedule}
            setModalToggle={() => {
              dispatch(
                setModalUiContent({
                  modal: 'schedule',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}

      {thumbnailStates.toggle && (
        <ModalBody
          onClose={() => {
            setShowThumbnailConf(true);
          }}
        >
          <ThumbnailPicker
            handleClose={() => {
              setShowThumbnailConf(true);
            }}
            handleCloseNoConfirmation={() => {
              dispatch(
                setModalUiContent({
                  modal: 'thumbnail',
                  attribute: 'toggle',
                  value: false
                })
              );
              if (!layeredThumbnail?.media?.original_asset) {
                dispatch(cancelImageThumbnail({ platform: thumbnailPlatform }));
              }
            }}
            platform={thumbnailStates.platform}
          />

          <ThumbnailEditorConfirmation
            handleClose={() => {
              setShowThumbnailConf(false);
            }}
            handleClear={() => {
              setShowThumbnailConf(false);
              dispatch(
                setModalUiContent({
                  modal: 'thumbnail',
                  attribute: 'toggle',
                  value: false
                })
              );
              if (!layeredThumbnail?.media?.original_asset) {
                dispatch(cancelImageThumbnail({ platform: thumbnailPlatform }));
              }
            }}
            show={showThumbnailConf}
          />
        </ModalBody>
      )}

      {overlayStates.toggle && (
        <ModalBody
          onClose={
            !OverlayEditorChanged
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'brandOverlay',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowOverlayConf(true)
          }
        >
          <OverlayCreator
            handleClose={
              !OverlayEditorChanged
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'brandOverlay',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                  }
                : () => setShowOverlayConf(true)
            }
            handleCloseNoConfirmation={() => {
              dispatch(
                setModalUiContent({
                  modal: 'brandOverlay',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />

          <OverlayEditorConfirmation
            handleClose={() => {
              setShowOverlayConf(false);
            }}
            handleClear={() => {
              setShowOverlayConf(false);
              dispatch(
                updateOverlayCreatorCloseState({
                  state: false
                })
              );
              dispatch(
                setModalUiContent({
                  modal: 'brandOverlay',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
            show={showOverlayConf}
          />
        </ModalBody>
      )}

      {browserPlugin.toggle && (
        <ModalBody
          onClose={
            browserPlugin?.allowClose
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'browserPlugin',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowBrowserPluginConf(true)
          }
        >
          <BrowserPluginModal
            handleClose={
              browserPlugin?.allowClose
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'browserPlugin',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                  }
                : () => setShowBrowserPluginConf(true)
            }
            handleCloseNoConf={() => {
              dispatch(
                setModalUiContent({
                  modal: 'browserPlugin',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />

          <EditorConfirmation
            handleClose={() => {
              setShowBrowserPluginConf(false);
            }}
            handleClear={() => {
              setShowBrowserPluginConf(false);
              dispatch(
                setModalUiContent({
                  modal: 'browserPlugin',
                  attribute: 'allowClose',
                  value: true
                })
              );
              dispatch(
                setModalUiContent({
                  modal: 'browserPlugin',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
            show={showBrowserPluginConf}
          />
        </ModalBody>
      )}

      {mediaStates.toggle && ( //TODO add the button above to buttonsBar component and add global state for edit image modal in ui
        <ModalBody
          onClose={
            !EditorChanged
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'mediaEditor',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowMedia(true)
          }
        >
          <ImagesEditor
            currentPlatform={mediaStates?.platform}
            currentIndex={mediaStates?.currentIndex}
            handleClose={
              !EditorChanged
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'mediaEditor',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                  }
                : () => setShowMedia(true)
            }
          />
        </ModalBody>
      )}
      <EditorConfirmation
        handleClose={() => {
          setShowMedia(false);

          dispatch(
            updateEditorCloseState({
              state: false
            })
          );
        }}
        handleClear={() => {
          setShowMedia(false);

          dispatch(
            setModalUiContent({
              modal: 'mediaEditor',
              attribute: 'toggle',
              value: false
            })
          );
          dispatch(
            setModalUiContent({
              modal: 'mediaEditor',
              attribute: 'currentIndex',
              value: null
            })
          );
          dispatch(
            updateEditorCloseState({
              state: false
            })
          );
        }}
        show={showMedia}
      />

      {mediaViewer.toggle && ( //TODO add the button above to buttonsBar component and add global state for edit image modal in ui
        <ModalBody
          onClose={() => {
            dispatch(
              setModalUiContent({
                modal: 'mediaViewer',
                attribute: 'toggle',
                value: false
              })
            );
          }}
        >
          <MediaView
            currentPlatform={mediaViewer?.platform}
            currentIndex={mediaViewer?.currentIndex}
            handleClose={() => {
              dispatch(
                setModalUiContent({
                  modal: 'mediaViewer',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}

      {brandMediaModal.toggle && ( //TODO add the button above to buttonsBar component and add global state for edit image modal in ui
        <ModalBody
          onClose={
            !BrandEditorChanged
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'brandMediaEditor',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                  dispatch(
                    setModalUiContent({
                      modal: 'brandMediaEditor',
                      attribute: 'cropMode',
                      value: false
                    })
                  );
                }
              : () => setShowBrandConf(true)
          }
        >
          <BrandImagesEditor
            currentPlatform={brandMediaModal?.platform}
            currentIndex={brandMediaModal?.currentIndex}
            cropMode={brandMediaModal?.cropMode}
            handleClose={
              !BrandEditorChanged
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'brandMediaEditor',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                    dispatch(
                      setModalUiContent({
                        modal: 'brandMediaEditor',
                        attribute: 'cropMode',
                        value: false
                      })
                    );
                  }
                : () => setShowBrandConf(true)
            }
          />
        </ModalBody>
      )}
      <EditorConfirmation
        handleClose={() => {
          setShowBrandConf(false);

          dispatch(
            updateBrandEditorCloseState({
              state: false
            })
          );
        }}
        handleClear={() => {
          setShowBrandConf(false);
          dispatch(
            setModalUiContent({
              modal: 'brandMediaEditor',
              attribute: 'toggle',
              value: false
            })
          );
          dispatch(
            setModalUiContent({
              modal: 'brandMediaEditor',
              attribute: 'currentIndex',
              value: null
            })
          );
          dispatch(
            setModalUiContent({
              modal: 'brandMediaEditor',
              attribute: 'cropMode',
              value: false
            })
          );
          dispatch(
            updateBrandEditorCloseState({
              state: false
            })
          );
        }}
        show={showBrandConf}
      />

      {mediaAltTagsStates?.toggle && ( //TODO add the button above to buttonsBar component and add global state for edit image modal in ui
        <ModalBody
          onClose={
            !EditorChanged
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'mediaAltTags',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowAltTagsConf(true)
          }
        >
          <MediaAltTags
            currentPlatform={mediaAltTagsStates?.platform}
            currentIndex={mediaAltTagsStates?.currentIndex}
            handleClose={
              mediaAltTagsStates?.allowClose
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'mediaAltTags',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                  }
                : () => setShowAltTagsConf(true)
            }
            handleCloseNoConf={() => {
              dispatch(
                setModalUiContent({
                  modal: 'mediaAltTags',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}
      <EditorConfirmation
        handleClose={() => {
          setShowAltTagsConf(false);
        }}
        handleClear={() => {
          setShowAltTagsConf(false);
          dispatch(
            setModalUiContent({
              modal: 'mediaAltTags',
              attribute: 'toggle',
              value: false
            })
          );
          dispatch(
            setModalUiContent({
              modal: 'mediaAltTags',
              attribute: 'currentIndex',
              value: null
            })
          );

          dispatch(
            setModalUiContent({
              modal: 'mediaAltTags',
              attribute: 'allowClose',
              value: true
            })
          );
        }}
        show={showAltTagsConf}
      />

      {multiAutoMediaModal.toggle && (
        <ModalBody
          onClose={
            !multiEditorChanged
              ? () => {
                  dispatch(
                    clearEditedMediafile({
                      platform: ALL
                    })
                  );
                  dispatch(
                    setModalUiContent({
                      modal: 'multiMediaEditor',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                }
              : () => setShowEditorConfirmation(true)
          }
        >
          <MultiImageModifier
            handleClose={
              !multiEditorChanged
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'multiMediaEditor',
                        attribute: 'toggle',
                        value: false
                      })
                    );

                    dispatch(
                      clearEditedMediafile({
                        platform: ALL
                      })
                    );
                  }
                : () => setShowEditorConfirmation(true)
            }
            platform={multiAutoMediaModal.platform}
            handleCloseNoConfirmation={() => {
              dispatch(
                setModalUiContent({
                  modal: 'multiMediaEditor',
                  attribute: 'toggle',
                  value: false
                })
              );
              dispatch(
                clearEditedMediafile({
                  platform: ALL
                })
              );
            }}
            newUploadedAsset={platformPostData?.unEditedMedia}
            count={
              platformPostData.media?.length -
              platformPostData.unEditedMedia?.length
            }
          />
        </ModalBody>
      )}
      <EditorConfirmation
        handleClose={() => setShowEditorConfirmation(false)}
        handleClear={() => {
          dispatch(
            setModalUiContent({
              modal: 'multiMediaEditor',
              attribute: 'toggle',
              value: false
            })
          );
          dispatch(
            updateMultiImageEditorCloseState({
              state: false
            })
          );
          dispatch(
            clearEditedMediafile({
              platform: ALL
            })
          );
        }}
        show={showEditorConfirmation}
      />

      {IGTagModalState.toggle && (
        <ModalBody
          onClose={() => {
            dispatch(
              setModalUiContent({
                modal: 'IGTags',
                attribute: 'toggle',
                value: false
              })
            );
          }}
        >
          <IGTags
            handleClose={() => {
              dispatch(
                setModalUiContent({
                  modal: 'IGTags',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}

      {/* IGProductTags Modal */}
      {IGProductTagModalState.toggle && (
        <ModalBody
          onClose={
            IGProductTagModalState.allowClose
              ? () => {
                  dispatch(
                    setModalUiContent({
                      modal: 'IGProductTags',
                      attribute: 'toggle',
                      value: false
                    })
                  );
                  dispatch(
                    setModalUiContent({
                      modal: 'IGProductTags',
                      attribute: 'allowClose',
                      value: true
                    })
                  );
                }
              : () => setProductTagConf(true)
          }
        >
          <IGProductTags
            handleClose={
              IGProductTagModalState.allowClose
                ? () => {
                    dispatch(
                      setModalUiContent({
                        modal: 'IGProductTags',
                        attribute: 'toggle',
                        value: false
                      })
                    );
                    dispatch(
                      setModalUiContent({
                        modal: 'IGProductTags',
                        attribute: 'allowClose',
                        value: true
                      })
                    );
                  }
                : () => setProductTagConf(true)
            }
          />
        </ModalBody>
      )}
      <EditorConfirmation
        handleClose={() => setProductTagConf(false)}
        handleClear={() => {
          setProductTagConf(false);

          dispatch(
            setModalUiContent({
              modal: 'IGProductTags',
              attribute: 'toggle',
              value: false
            })
          );

          dispatch(
            setModalUiContent({
              modal: 'IGProductTags',
              attribute: 'allowClose',
              value: true
            })
          );
        }}
        show={productTagConf}
      />

      {/* InstagramMusic Modal */}
      {IGMusicModalState?.toggle && (
        <ModalBody
          onClose={() => {
            dispatch(
              setModalUiContent({
                modal: 'IGMusic',
                attribute: 'toggle',
                value: false
              })
            );
          }}
        >
          <InstagramMusic
            handleClose={() => {
              dispatch(
                setModalUiContent({
                  modal: 'IGMusic',
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}

      {/* ContentTextBlocks Modal */}
      {textBlock.toggle && (
        <ModalBody
          onClose={() => {
            dispatch(
              setTextBlockModalUi({
                attribute: 'toggle',
                value: false
              })
            );
          }}
        >
          <ContentTextBlocks
            currentPlatform={textBlock?.platform}
            handleClose={() => {
              dispatch(
                setTextBlockModalUi({
                  attribute: 'toggle',
                  value: false
                })
              );
            }}
          />
        </ModalBody>
      )}
    </div>
  );
};

export default CreatorMainModals;
